import React from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

// Average shape of a fill across every sold-out break in range. y is a sale's
// price as a % of its own break's average spot (100 = that break's average),
// x is how far through the fill it sold. The band is the middle half of
// breaks at each point. See getSpotPriceCurve on the server for the math.

const LINE_COLOR = '#3987e5';
const BAND_COLOR = '#3987e5';

function CurveTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const b = payload[0].payload;
  if (b.avg_pct == null) return null;
  const diff = b.avg_pct - 100;
  return (
    <div style={{ background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{b.label} through the fill</div>
      <div>Avg {b.avg_pct}% of break avg ({diff >= 0 ? '+' : ''}{diff.toFixed(1)}%)</div>
      <div style={{ color: 'var(--text-light)' }}>Middle half of breaks: {b.p25}% – {b.p75}%</div>
      <div style={{ color: 'var(--text-light)' }}>{b.breaks} break{b.breaks === 1 ? '' : 's'}</div>
    </div>
  );
}

export default function SpotPriceCurve({ data, height = 260 }) {
  if (!data) return <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Loading…</div>;
  if (!data.breaks_included) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-light)', padding: '8px 0' }}>
        No sold-out breaks with logged spot sales in this range.
      </div>
    );
  }

  const buckets = data.buckets;
  const first = buckets[0]?.avg_pct;
  const last = buckets[buckets.length - 1]?.avg_pct;
  const values = buckets.flatMap(b => [b.p25, b.p75, b.avg_pct]).filter(v => v != null);
  // Keep 100 in view and pad a little so the band never touches the frame.
  const lo = Math.floor(Math.min(100, ...values) / 10) * 10 - 10;
  const hi = Math.ceil(Math.max(100, ...values) / 10) * 10 + 10;
  const skipped = [
    data.breaks_too_few_sales > 0 && `${data.breaks_too_few_sales} with fewer than ${data.min_sales} logged sales left out`,
    data.breaks_reconstructed > 0 && `${data.breaks_reconstructed} rebuilt from saved spot times (approximate)`,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Breaks</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{data.breaks_included}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Opening spots</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{first}%</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Closing spots</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{last}%</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, fontSize: 11, color: 'var(--text-light)', marginLeft: 'auto' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 2, background: LINE_COLOR, display: 'inline-block' }} /> Average
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 10, background: BAND_COLOR, opacity: 0.18, display: 'inline-block', borderRadius: 2 }} /> Middle half of breaks
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={buckets} margin={{ top: 8, right: 28, left: 0, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0}
            label={{ value: 'Through the fill', position: 'insideBottomRight', offset: -2, fontSize: 11 }} height={34} />
          <YAxis tick={{ fontSize: 11 }} width={48} domain={[lo, hi]} tickFormatter={v => `${v}%`} />
          <Tooltip content={<CurveTooltip />} />
          <ReferenceLine y={100} stroke="var(--text-light)" strokeDasharray="4 3"
            label={{ value: 'Break avg', position: 'insideTopLeft', fontSize: 10, fill: 'var(--text-light)' }} />
          <Area type="monotone" dataKey="band" stroke="none" fill={BAND_COLOR} fillOpacity={0.18}
            isAnimationActive={false} activeDot={false} />
          <Line type="monotone" dataKey="avg_pct" stroke={LINE_COLOR} strokeWidth={2}
            dot={{ r: 3, fill: LINE_COLOR, strokeWidth: 0 }} activeDot={{ r: 5 }} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
      {skipped && <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>{skipped}</div>}
    </div>
  );
}
